import type { Attachment } from "../../domain/app-types.ts";
import { escapeHtml } from "../../ui/text.ts";
import { attachmentListView } from "./view.ts";
import { bindActions } from "../../app/actions.ts";
import { createAttachmentUploader } from "./upload-controller.ts";
import { createAttachmentPreview } from "./preview-controller.ts";

interface AttachmentDependencies {
  request: (path: string, init?: RequestInit) => Promise<Response | null>;
  refreshTask: (id: string) => Promise<void>;
  confirmDelete: () => Promise<boolean>;
  errorMessage: (response: Response, fallback: string) => Promise<string>;
  toast: (message: string, error?: boolean) => void;
  icon: (name: string) => string;
  online: () => boolean;
}

export interface AttachmentController {
  count(taskId: string): number;
  loaded(taskId: string): boolean;
  view(taskId: string, editable: boolean): string;
  load(taskId: string): Promise<void>;
  bind(root: ParentNode): void;
}

export function formatAttachmentBytes(bytes: number): string { return bytes < 1024 ? `${bytes} B` : bytes < 1_048_576 ? `${(bytes / 1024).toFixed(1)} KB` : `${(bytes / 1_048_576).toFixed(1)} MB`; }

export function createAttachmentController(deps: AttachmentDependencies): AttachmentController {
  const cache = new Map<string, Attachment[]>(), versions = new Map<string, number>();
  const inFlight = new Map<string, Promise<void>>();
  const slowLoads = new Set<string>(), loadErrors = new Set<string>(), deleting = new Set<string>(), downloading = new Set<string>();
  const remember = (taskId: string, items: Attachment[]) => { cache.set(taskId, items); versions.set(taskId, (versions.get(taskId) || 0) + 1); };
  const uploader = createAttachmentUploader({
    ...deps,
    remember: (taskId, item) => remember(taskId, [...(cache.get(taskId) || []).filter(existing => existing.id !== item.id), item]),
    refreshFiles: async id => { await controller.load(id); if (loadErrors.has(id)) throw new Error("Could not refresh files"); },
  });
  const preview = createAttachmentPreview(deps);
  const download = async (button: HTMLButtonElement) => {
    const taskId = button.dataset.downloadAttachments || "";
    if (downloading.has(taskId)) return;
    if (!deps.online()) { deps.toast("Connect to download attachments", true); return; }
    downloading.add(taskId); button.disabled = true; const failed: string[] = [];
    try {
      for (const item of cache.get(taskId) || []) {
        try {
          const response = await deps.request(`/api/v1/tasks/${taskId}/attachments/${item.id}`, { credentials: "include" });
          if (!response?.ok) throw new Error("Download unavailable");
          const url = URL.createObjectURL(await response.blob());
          const link = button.ownerDocument.createElement("a"); link.href = url; link.download = item.file_name;
          try { link.click(); } finally { setTimeout(() => URL.revokeObjectURL(url), 1000); }
        } catch { failed.push(item.file_name); }
      }
      if (failed.length) deps.toast(`Could not start downloads for: ${failed.join(", ")}. Downloading these files again is safe.`, true);
    } finally { downloading.delete(taskId); button.disabled = false; }
  };
  const remove = async (button: HTMLButtonElement) => {
    const id = button.dataset.deleteAttachment || "", taskId = button.dataset.task || "", key = taskId + ":" + id;
    if (deleting.has(key)) return; deleting.add(key); button.disabled = true;
    try {
      if (!await deps.confirmDelete()) return;
      const response = await deps.request(`/api/v1/tasks/${taskId}/attachments/${id}`, { method: "DELETE", credentials: "include" });
      if (!response?.ok) throw new Error(response ? await deps.errorMessage(response, "Could not delete attachment") : "Deletion could not be confirmed. Refresh files to check.");
      remember(taskId, (cache.get(taskId) || []).filter(item => item.id !== id));
      deps.toast("Attachment deleted");
      try { await deps.refreshTask(taskId); } catch { deps.toast("Attachment deleted, but the view could not refresh. Reopen the task to check it.", true); }
    } catch (error) { deps.toast(error instanceof Error ? error.message : "Deletion could not be confirmed. Refresh files to check.", true); }
    finally { deleting.delete(key); button.disabled = false; }
  };
  const controller: AttachmentController = {
    count: id => cache.get(id)?.length || 0,
    loaded: id => cache.has(id) || loadErrors.has(id),
    view(taskId, editable) {
      const attachments = cache.get(taskId);
      const error = loadErrors.has(taskId) ? `<p class="attachment-load-error" role="alert">${attachments ? "Could not refresh files. Showing the last known list." : "Could not load files."} <button type="button" class="text-button" data-retry-attachments="${escapeHtml(taskId)}">Retry</button></p>` : "";
      return attachments ? attachmentListView(taskId, attachments, editable, deps.online(), deps.icon, formatAttachmentBytes) + error : error || (slowLoads.has(taskId) ? '<p class="muted" role="status">Loading attachments…</p>' : "");
    },
    load(taskId) {
      const current = inFlight.get(taskId); if (current) return current;
      const version = versions.get(taskId) || 0;
      if (loadErrors.delete(taskId)) void deps.refreshTask(taskId).catch(() => undefined);
      const timer = setTimeout(() => { if (inFlight.has(taskId) && !cache.has(taskId)) { slowLoads.add(taskId); void deps.refreshTask(taskId).catch(() => undefined); } }, 250);
      const pending = (async () => {
        try {
          const response = await deps.request(`/api/v1/tasks/${taskId}/attachments`, { credentials: "include" });
          if (!response?.ok) throw new Error("Could not load attachments");
          const items = await response.json() as Attachment[]; if (!Array.isArray(items)) throw new Error("Invalid file list");
          // A GET started before a confirmed local upload/deletion must not undo it.
          if ((versions.get(taskId) || 0) === version) cache.set(taskId, items);
        } catch { loadErrors.add(taskId); }
        finally {
          clearTimeout(timer); slowLoads.delete(taskId); inFlight.delete(taskId);
          try { await deps.refreshTask(taskId); } catch { deps.toast("The file view could not refresh. Reopen the task to check it.", true); }
        }
      })();
      inFlight.set(taskId, pending); return pending;
    },
    bind(root) {
      bindActions(root, () => undefined, "[data-attachment-command]");
      const bind = (selector: string, handler: (button: HTMLButtonElement) => void) => root.querySelectorAll<HTMLButtonElement>(selector).forEach(button => {
        if (button.dataset.bound) return; button.dataset.bound = "true"; button.addEventListener("click", () => handler(button));
      });
      root.querySelectorAll<HTMLElement>(".attachment-menu [role=menuitem]").forEach(button => {
        if (button.dataset.menuBound) return; button.dataset.menuBound = "true";
        button.addEventListener("click", () => { const menu = button.closest<HTMLDetailsElement>("details")!; menu.open = false; menu.querySelector<HTMLElement>("summary")?.focus(); });
      });
      bind("[data-retry-attachments]", button => { void controller.load(button.dataset.retryAttachments || ""); });
      root.querySelectorAll<HTMLFormElement>(".attachment-form").forEach(form => uploader.bind(form));
      bind("[data-download-attachments]", button => { void download(button); });
      bind("[data-preview-attachment]", button => preview(button));
      bind("[data-delete-attachment]", button => { void remove(button); });
    },
  };
  return controller;
}
